import { cloneDeep } from 'lodash';
import { paramTemplateTypes } from 'scenes/audiences/NewAudience/ParamTemplates/ParamTemplates';
import * as yup from 'yup';

const includes = 'incParams';
const excludes = 'excParams';

const initParam = paramTemplateTypes[0];

const emptyRule = {
  id: null,
  name: 'Custom Key',
  key: '',
  kv_relational_op: 'eq',
  sort: null,
  value: '',
  value_type: 'str',
};

export const parseOperator = operator => {
  const operators = {
    ge: 'Greater/Equal to',
    le: 'Less/Equal to',
    gt: 'Greater than',
    lt: 'Less than',
    eq: 'Equal to',
    ne: 'Not equal to',
    // eq: 'On',
    // le: 'On or before',
    // lt: 'Before',
    // ge: 'On or after',
    // gt: 'After',
    // ne: 'Not on',
    // eq: 'Is',
    // ne: 'Is not',
    con: 'Contains',
    starts: 'Starts with',
  };

  return operators[operator] || operator;
};

export const setInitialAudienceForm = () => ({
  name: '',
  type: 'Data Params',
  incParams: [],
  excParams: [],
  rule_sets: [],
});

const rulesSchema = yup.array().of(
  yup.object().shape({
    value_type: yup.string(),
    name: yup.string(),
    value: yup.string().when(['value_type', 'kv_relational_op'], {
      is: (type, op) =>
        (type === 'int' || type === 'str' || type === '[str]') &&
        (op === 'eq' || op === 'ne'),
      then: yup.string().nullable(),
      otherwise: yup.string().required('Value required.'),
    }),
    key: yup.string().required('Key required.'),
  })
);

export const setAudienceValidationSchema = () =>
  yup.object().shape({
    name: yup.string().required('Name is required.'),
    type: yup
      .string()
      .nullable()
      .matches(/(Data Params)/, 'Only Data Params is supported currently.'),
    incParams: yup.array().of(
      yup.object().shape({
        rules: rulesSchema,
      })
    ),
    excParams: yup.array().of(
      yup.object().shape({
        rules: rulesSchema,
      })
    ),
  });

export const loadAudience = ({ audience }) => {
  const incParamSets = [
    ...audience.rule_sets.filter(ruleSet => ruleSet.logical_op_type === true),
  ];

  const excParamSets = [
    ...audience.rule_sets.filter(ruleSet => ruleSet.logical_op_type === false),
  ];

  return { incParamSets, excParamSets };
};

/**
 * Ensure value_type is 'obj' for keys containing a dot (object paths)
 */
const ensureObjectTypeForDottedKeys = rule => {
  if (rule.key && rule.key.includes('.')) {
    rule.value_type = 'obj';
  }
};

export const buildAudience = ({
  audience,
  incParams,
  excParams,
  company_id,
}) => {
  // Deep clone to avoid mutating read-only Formik/RTK Query state
  const clonedIncParams = cloneDeep(incParams);
  const clonedExcParams = cloneDeep(excParams);

  const state_rs = [...clonedIncParams, ...clonedExcParams];

  clonedIncParams.forEach(ruleSet => {
    const emptyRuleSet = ruleSet.rules[0] === false;

    if (!emptyRuleSet) {
      ruleSet.rules.forEach(rule => {
        ensureObjectTypeForDottedKeys(rule);
        if (typeof rule.value == 'number') {
          rule.value = `${rule.value}`;
        } else if (rule.value === '') {
          rule.value = null;
        }
      });
    }
  });

  clonedExcParams.forEach(ruleSet => {
    const emptyRuleSet = ruleSet.rules[0] === false;

    if (!emptyRuleSet) {
      ruleSet.rules.forEach(rule => {
        ensureObjectTypeForDottedKeys(rule);
        if (typeof rule.value == 'number') {
          rule.value = `${rule.value}`;
        } else if (rule.value === '') {
          rule.value = null;
        }
      });
    }
  });

  return {
    payload: {
      ...audience,
      company_id: company_id,
      rule_sets: state_rs,
    },
  };
};

const includesGroup = group => group === includes;

export const addSet = ({ form, group }) => {
  const newSet = {
    logical_op_type: includesGroup(group),
    rules: [{ ...emptyRule }],
  };

  // Create a new updated group array (immutably)
  const updatedGroup = [...form.values[group], newSet];

  // Rebuild rule_sets from both groups
  const updatedIncParams =
    group === 'incParams' ? updatedGroup : form.values.incParams;
  const updatedExcParams =
    group === 'excParams' ? updatedGroup : form.values.excParams;

  const newForm = {
    ...form.values,
    [group]: cloneDeep(updatedGroup),
    rule_sets: cloneDeep([...updatedIncParams, ...updatedExcParams]),
  };

  return { newForm };
};

export const removeSet = ({ form, group, index }) => {
  const ruleSetGroup = form.values[group];

  ruleSetGroup.splice(index, 1);

  const ruleSets = [...form.values[includes], ...form.values[excludes]];
  const newForm = {
    ...form.values,
    [group]: cloneDeep(ruleSetGroup),
    rule_sets: cloneDeep(ruleSets),
  };

  return { newForm };
};

export const removeSet2 = ({ form, group, index }) => {
  const currentGroup = form.values[group];
  const updatedGroup = currentGroup.filter((_, i) => i !== index); // immutable removal

  // Recompute rule_sets (merged from both groups)
  const updatedIncParams =
    group === 'incParams' ? updatedGroup : form.values.incParams;
  const updatedExcParams =
    group === 'excParams' ? updatedGroup : form.values.excParams;

  const newForm = {
    ...form.values,
    [group]: cloneDeep(updatedGroup),
    rule_sets: cloneDeep([...updatedIncParams, ...updatedExcParams]),
  };

  return { newForm };
};

export const removeParam = ({
  initialForm,
  ruleSetGroup,
  ruleSetIndex,
  ruleIndex,
  group,
}) => {
  const clonedGroup = cloneDeep(ruleSetGroup); // Deep copy to avoid mutating form.values
  const rules = clonedGroup[ruleSetIndex].rules;

  const defaultParam =
    (group === includes &&
      clonedGroup.length === 1 &&
      rules.length === 1 &&
      initialForm.incParams.length > 0 &&
      initialForm.excParams.length === 0) ||
    (group === excludes &&
      clonedGroup.length === 1 &&
      rules.length === 1 &&
      initialForm.incParams.length === 0 &&
      initialForm.excParams.length > 0);

  if (defaultParam) {
    // Reset value only, don't remove
    clonedGroup[ruleSetIndex].rules[ruleIndex].value = '';
  } else {
    // Remove rule immutably
    clonedGroup[ruleSetIndex].rules = rules.filter((_, i) => i !== ruleIndex);

    // Remove empty rule set
    if (clonedGroup[ruleSetIndex].rules.length === 0) {
      clonedGroup.splice(ruleSetIndex, 1);
    }
  }

  // Ensure all rules have a defined `value`
  clonedGroup.forEach(ruleSet => {
    ruleSet.rules = ruleSet.rules.map(rule => ({
      ...rule,
      value: rule.value ?? '',
    }));
  });

  const updatedIncParams =
    group === 'incParams' ? clonedGroup : initialForm.incParams;
  const updatedExcParams =
    group === 'excParams' ? clonedGroup : initialForm.excParams;

  const newForm = {
    ...initialForm,
    [group]: clonedGroup,
    rule_sets: [...updatedIncParams, ...updatedExcParams],
  };

  return { newForm };
};
